import React from 'react';
import peakMedia from '../img/peak.png';
import sherbornFuel from '../img/sherbornFuel.jpg';
import cpk from '../img/cpk.jpeg';
import codewiz from '../img/codewiz.jpg';
import tenx from '../img/10x.jpg';
import ss from '../img/ss.jpg';
import gs from '../img/gs.jpg';
import du from '../img/du.jpg';
import dt from '../img/dt.jpg';

const BracketBullet: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="text-gray-700 mt-3 relative leading-relaxed bg-gradient-to-r from-indigo-50 via-blue-50 to-cyan-50 border-l-[3px] border-indigo-500 rounded-md px-4 py-3.5 -ml-[84px] shadow-md">
        {children}
    </div>
);

interface JobCardProps {
    logo: string;
    logoAlt: string;
    title: string;
    company: string;
    year: string;
    type: string;
    bullets?: string[];
    description?: string;
}

const JobCard: React.FC<JobCardProps> = ({ logo, logoAlt, title, company, year, type, bullets, description }) => (
    <div className="max-w-4xl w-full bg-white rounded-xl shadow-xl p-6 transform transition-all duration-500 border-l-4 border-blue-400">
        <div className="flex items-start gap-4">
            <img src={logo} alt={logoAlt} className="w-16 h-16 flex-shrink-0 rounded-xl shadow-md" />
            <div className="relative flex-grow">
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{title}</h3>
                <p className="text-gray-700 font-medium text-lg">{company}</p>
                <div className="flex gap-3 items-center text-sm text-gray-500 mt-1.5">
                    <span className="italic font-medium">{year}</span>
                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 group-hover:bg-cyan-400 transition-colors duration-200"></span>
                    <span className="italic">{type}</span>
                </div>
                
                {bullets && bullets.length > 0 && (
                    <div className="mt-6 space-y-3">
                        {bullets.map((bullet, index) => (
                            <BracketBullet key={index}>{bullet}</BracketBullet>
                        ))}
                    </div>
                )}
                
                {description && (
                    <div className="mt-6 space-y-3">
                        <BracketBullet>{description}</BracketBullet>
                    </div>
                )}
            </div>
        </div>
    </div>
);

const Work = () => {
    const jobs = [
        {
            logo: tenx,
            logoAlt: "10x Logo",
            title: "Software Engineer",
            company: "10x People",
            year: "2025-Present",
            type: "Full time",
            bullets: [
                "Driving a quarterly cross-team initiative to define and implement standardized backend patterns, improving consistency, maintainability, and accelerating feature delivery across services.",
                "Instrumented backend systems for distributed tracing and application performance monitoring using OpenTelemetry and the Grafana stack on AWS EKS, deployed via CDK- and Helm-managed infrastructure.",
                "Modernized legacy Java backend applications (Spring, Tomcat), upgrading runtime components and integrating AWS services including S3, SNS, EFS, and RDS.",
                "Migrated monolithic EC2-hosted services to ECS Fargate with CDK-defined infrastructure, establishing scalable, maintainable backend architectures for faster feature delivery.",
                "Designed and automated ingress, service routing, and networking using HAProxy, VPC peering, internal Route 53 hosted zones, and security groups, while building end-to-end CI/CD pipelines with GitHub Actions to test, migrate databases, and deploy services with confidence."
            ]
        },
        {
            logo: dt,
            logoAlt: "Dynatrace Logo",
            title: "Product Specialist Intern",
            company: "Dynatrace",
            year: "Summer 2024",
            type: "Full Time",
            bullets: [
                "Gained experience deploying Dynatrace software with k8s",
                "Developed presentation skills with frequent pitches and mock client meetings",
                "Acquired APM & observability software expertise",
                "Increased client success by resolving configuration problems"
            ]
        },
        {
            logo: du,
            logoAlt: "DU Logo",
            title: "Undergraduate Teaching Assistant",
            company: "University of Denver",
            year: "2024",
            type: "Intro to Machine Learning",
            description: "Assisted Dr. Mario Lopez in grading for his class, Intro to Machine Learning."
        },
        {
            logo: gs,
            logoAlt: "Golfsnake Logo",
            title: "Full Stack Developer",
            company: "Golfsnake",
            year: "2023-2024",
            type: "Independent contractor",
            bullets: [
                "Responsible for maintaining Golfsnake, a website intended to find tee times at a smaller interval than competitors, providing all tee times in given areas.",
                "Resolved UI/UX issues, implemented new content, and designed internal tools, ultimately bringing in 1500+ users."
            ]
        },
        {
            logo: ss,
            logoAlt: "Snow Shuttle Logo",
            title: "Full Stack Developer / Platform Engineer",
            company: "Snow Shuttle",
            year: "2023",
            type: "Startup",
            bullets: [
                "Responsible for the deployment, upkeep, and development of Snow Shuttle, a student led carpooling startup.",
                "Acquired 100+ users and facilitated more than 15 rides to the mountains, utilizing the microgrant that we received from DU Entrepreneurship."
            ]
        },
        {
            logo: tenx,
            logoAlt: "10x Logo",
            title: "Software Engineering Intern",
            company: "10x People",
            year: "2023",
            type: "Summer internship",
            bullets: [
                "Developed secure telecommunications software within multiple teams.",
                "Incredible experience with CI/CD pipelining, data manipulation, authorization, AWS, Agile, Python/Java web frameworks, and testing.",
                "Designed and implemented communications workflow synchronizing data across multiple platforms, reducing overall system load."
            ]
        },
        {
            logo: codewiz,
            logoAlt: "Code Wiz Logo",
            title: "Coding Coach",
            company: "Code Wiz",
            year: "2022",
            type: "Summer job",
            bullets: [
                "Created an engaging environment for students (ages 7-13) to learn Unity and Unity VR game development with C#.",
                "Taught LEGO robotics and Minecraft modding.",
                "Worked with corporate management to upgrade and modify existing Unity curriculum."
            ]
        },
        {
            logo: cpk,
            logoAlt: "CPK Logo",
            title: "Server",
            company: "California Pizza Kitchen",
            year: "2021",
            type: "Summer job",
            description: "Improved soft-skills while enhancing customer experience."
        },
        {
            logo: sherbornFuel,
            logoAlt: "Sherborn Fuel Logo",
            title: "Attendant",
            company: "Sherborn Fuel",
            year: "2021",
            type: "Part-time high school gig",
            description: "Improved soft-skills while enhancing customer experience."
        },
        {
            logo: peakMedia,
            logoAlt: "Peak Media Logo",
            title: "Technology Manager",
            company: "Peak Media LLC",
            year: "2020-2021",
            type: "High school startup",
            bullets: [
                "Boosted company revenue by acquiring new customers via cold calling and deploying a new website.",
                "Pitched to school board to offer sports livestreaming during COVID-19 using Wowza services.",
                "Researched and developed infrastructure to release NFT's on the Ethereum blockchain."
            ]
        }
    ];

    return (
        <div>
            <div className="min-h-screen flex flex-col items-center justify-center">
                <div id="work-experience" className="mt-20 p-6 pt-10 space-y-6 mb-10">
                    <h2 className="text-3xl font-bold text-white text-center">Work Experience</h2>
                    {jobs.map((job, index) => (
                        <JobCard key={index} {...job} />
                    ))}
                </div>
            </div>
            {/* <CanvasBackground /> */}
        </div>
    );
};

export default Work;
